import React, { useEffect, useState } from "react";
import {
  Box,
  ButtonBase,
  Chip,
  FormControlLabel,
  InputBase,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  BACKEND_TIMEZONE,
  convertEstToLocalTimezone,
  formatCount,
  handleErrorMessage,
  isTruthy,
  isUnlimitedLimit,
  openErrorNotification,
} from "helpers/methods";
import { getAppPlanLimitByEntity } from "../SignRequestService";
import strings from "global/constants/StringConstants";
import urls from "global/constants/UrlConstants";
import moment, { Moment } from "moment";
import EmailPreviewComponent from "./EmailPreviewComponent";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import {
  setErrors,
  setReduxSignRequest,
  setScheduleTime,
  updateIndividualSigner,
  updateSelfSignedFlag,
} from "redux/appSlice";
import { getExpirationDays } from "screens/Configurations/ConfigurationsService";
import UnlimitedLabel from "global/components/UnlimitedLabel/UnlimitedLabel";
import { AppModal } from "global/components/AppModal";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import MyDateTimePicker from "global/components/CustomDatePicker/MyDateTimePicker";
import { PlanLimit, RequestParticipant } from "models/interfaces";
import { fetchAccountDetails } from "Services/CommonServices";
import { useFeatureState, FeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import UpgradeModal from "global/components/UpgradeModal/UpgradeModal";
import {
  fetchWebFormsByTemplate,
  WebForm,
} from "screens/WebForms/WebFormsService";
import CustomAutoComplete from "global/components/CustomAutoComplete/CustomAutoComplete";
import emailConfigurationsStyles from "../EmailConfigurations.styles";
import emailConfigurationsTokens from "../EmailConfigurations.tokens";
import EmailConfigSection from "./EmailConfigSection";
import { isEmbedEditorMode } from "embed/EmbedEditorMode";
import EmailConfigSwitch from "./EmailConfigSwitch";
import {
  CalendarGlyph,
  CalendarSmallGlyph,
  ClockGlyph,
  DocumentGlyph,
  MailGlyph,
  PeopleGlyph,
  ShieldCheckGlyph,
} from "./EmailConfigIcons";

const styles = emailConfigurationsStyles;
const { schedule, lock, layout } = emailConfigurationsTokens;

const SUBJECT_MAX = 100;
const MESSAGE_MAX = 1000;

type SectionId =
  | "content"
  | "reminders"
  | "options"
  | "schedule"
  | "esign"
  | "webforms";

/** The four design sections open exactly as `Email Configurations.dc.html`. */
const DEFAULT_OPEN: Record<SectionId, boolean> = {
  content: true,
  reminders: true,
  options: false,
  schedule: false,
  esign: false,
  webforms: false,
};

/** Signer-permission rows, in the design's order. */
const PERMISSIONS = [
  {
    key: "allowDocumentDownload",
    label: "Download documents",
    desc: "Signers can download the document",
  },
  {
    key: "sendNotificationOnCompletion",
    label: "Completion notification",
    desc: "Email signers when the request completes",
  },
  {
    key: "attachDocumentToMailOnCompletion",
    label: "Attach signed copies",
    desc: "Include signed documents in the completion email",
  },
  {
    key: "allowSignatureDelegation",
    label: "Delegation",
    desc: "Signers can delegate signing to someone else",
  },
] as const;

const RequestConfiguration = (props: {
  useTemplate?: boolean;
  updateTemplate?: boolean;
  copyRequest?: boolean;
  editDraft?: boolean;
  setOpenDatePicker: Function;
  setIsScheduleRequest: Function;
  openDatePicker: boolean;
  isScheduleReq: boolean;
  signerToDelay: Partial<RequestParticipant>;
  setIsDelaySigner: Function;
  setLoader: Function;
}) => {
  const dispatch = useAppDispatch();
  const { signRequest, errors } = useAppSelector(
    (state: RootState) => state.app,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [isDisable, setIsDisable] = useState<boolean>(true);
  const [selectedValue, setSelectedValue] = useState(
    signRequest.selfSigned ? strings.selfSigned : strings.certifiedSign,
  );
  const [accountDetails, setAccountDetails] = useState<any>();
  const [upgradeModal, setUpgradeModal] = useState<{
    open: boolean;
    featureKey?: string;
  }>({ open: false });
  const [open, setOpen] = useState<Record<SectionId, boolean>>(DEFAULT_OPEN);
  /**
   * "Schedule for later" stays selected while the date dialog is open or after
   * it is dismissed without a pick, so the strip does not flicker back to
   * "Send immediately" before a time exists.
   */
  const [wantsSchedule, setWantsSchedule] = useState<boolean>(
    isTruthy(signRequest.scheduleTime),
  );

  const [useWebForms, setUseWebForms] = useState<boolean>(
    (signRequest.signers || []).some((s: any) => isTruthy(s.webFormId)),
  );
  const [availableWebForms, setAvailableWebForms] = useState<WebForm[]>([]);
  const [webFormsLoading, setWebFormsLoading] = useState<boolean>(false);
  const isFromTemplate = isTruthy(signRequest.templateId);

  const scheduleState = useFeatureState(FeatureKeys.ENVELOPE_SCHEDULED_SEND);
  const reminderState = useFeatureState(FeatureKeys.REMINDER_AUTOMATIC);
  const delegationState = useFeatureState(FeatureKeys.ENVELOPE_DELEGATION);
  const autoExpirationState = useFeatureState(
    FeatureKeys.ENVELOPE_AUTO_EXPIRATION,
  );
  const webFormsState = useFeatureState(FeatureKeys.ENVELOPE_WEB_FORMS);
  const isScheduleAvailable =
    scheduleState.status === strings.FEATURE_STATUS_AVAILABLE;
  const isAutoExpirationAvailable =
    autoExpirationState.status === strings.FEATURE_STATUS_AVAILABLE;
  const isReminderAvailable =
    reminderState.status === strings.FEATURE_STATUS_AVAILABLE;
  const isDelegationAvailable =
    delegationState.status === strings.FEATURE_STATUS_AVAILABLE;
  const isWebFormsAvailable =
    webFormsState.status === strings.FEATURE_STATUS_AVAILABLE;

  const getFeatureTooltip = (state: FeatureState): string => {
    if (state.status === strings.FEATURE_STATUS_NOT_IN_PLAN)
      return strings.FEATURE_GATE_UPSELL_TOOLTIP;
    if (state.status === strings.FEATURE_STATUS_ADMIN_DISABLED)
      return (
        state.disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP
      );
    return "";
  };

  const selfSignedStats = accountDetails?.appPlanLimitList?.find(
    (plan: PlanLimit) => plan.entity === strings.selfSignedEnveloped,
  );
  const CertifiedSignedStats = accountDetails?.appPlanLimitList?.find(
    (plan: any) => plan.entity === strings.notariusVerifiedEnvelope,
  );
  const selfSignRemaining =
    (selfSignedStats?.limit ?? 0) - (selfSignedStats?.current ?? 0);
  const certifiedRemaining =
    (CertifiedSignedStats?.limit ?? 0) - (CertifiedSignedStats?.current ?? 0);

  useEffect(() => {
    // The embedded wizard has no plan-limit or billing session; it never offers the eSign mode choice.
    // `loading` starts true and only the plan-limit call clears it, so clear it here or the step stays a skeleton.
    if (isEmbedEditorMode()) {
      setLoading(false);
      return;
    }
    handledCertificateAccess();
    getAccountDetails();
  }, []);

  useEffect(() => {
    if (urls.PROD && signRequest.selfSigned) {
      dispatch(updateSelfSignedFlag(false));
      setSelectedValue(strings.certifiedSign);
    }
  }, []);

  useEffect(() => {
    if (
      !props.useTemplate &&
      !props.updateTemplate &&
      !props.copyRequest &&
      !props.editDraft &&
      !signRequest.id
    )
      getConfigurationData();
  }, []);

  useEffect(() => {
    if (isTruthy(signRequest.scheduleTime)) setWantsSchedule(true);
  }, [signRequest.scheduleTime]);

  useEffect(() => {
    if (!isFromTemplate || !signRequest.templateId) return;
    let cancelled = false;
    setWebFormsLoading(true);
    fetchWebFormsByTemplate(signRequest.templateId)
      .then((forms) => {
        if (cancelled) return;
        const activeForms = (forms || []).filter((f) => f.status === "Active");
        setAvailableWebForms(activeForms);
      })
      .catch(() => {
        if (cancelled) return;
        setAvailableWebForms([]);
      })
      .finally(() => {
        if (cancelled) return;
        setWebFormsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isFromTemplate, signRequest.templateId]);

  const toggleSection = (id: SectionId) =>
    setOpen((prev) => ({ ...prev, [id]: !prev[id] }));

  const getWebFormsForSigner = (signer: RequestParticipant): WebForm[] =>
    availableWebForms.filter((f) => Number(f.signerId) === Number(signer.id));

  const handleToggleWebForms = (checked: boolean) => {
    setUseWebForms(checked);
    if (!checked) {
      (signRequest.signers || []).forEach((s: any) => {
        if (isTruthy(s.webFormId)) {
          dispatch(
            updateIndividualSigner({
              signerId: s.id,
              fieldsToUpdate: { webFormId: null },
            }),
          );
        }
      });
    }
  };

  const handleWebFormChange = (signerId: number, webFormId: string) => {
    dispatch(
      updateIndividualSigner({
        signerId,
        fieldsToUpdate: { webFormId: webFormId || null },
      }),
    );
  };

  const getAccountDetails = async () => {
    try {
      const accDetails = await fetchAccountDetails();
      setAccountDetails(accDetails);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const handledCertificateAccess = async () => {
    try {
      setLoading(true);
      props.setLoader(true);
      setSelectedValue(
        signRequest.selfSigned ? strings.selfSigned : strings.certifiedSign,
      );
      const [selfSigned, notariusVerified] = await Promise.all([
        getAppPlanLimitByEntity(strings.selfSignedEnveloped),
        getAppPlanLimitByEntity(strings.notariusVerifiedEnvelope),
      ]);
      const isNotariusVerifiedEntityPresent =
        notariusVerified.hasOwnProperty("entity") &&
        notariusVerified.hasOwnProperty("limit");

      if (isNotariusVerifiedEntityPresent && notariusVerified["limit"] > 0) {
        setIsDisable(false);
      } else {
        setSelectedValue(strings.selfSigned);
        dispatch(updateSelfSignedFlag(true));
      }
      setLoading(false);
      props.setLoader(false);
    } catch (error: any) {
      setLoading(false);
      props.setLoader(false);
      handleErrorMessage(error);
    }
  };

  const getConfigurationData = async () => {
    try {
      const expirationDaysRes = await getExpirationDays();
      dispatch(setReduxSignRequest({ expirationDays: expirationDaysRes }));
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const setSignType = (value: string) => {
    dispatch(updateSelfSignedFlag(value === strings.selfSigned));
    setSelectedValue(value);
  };

  const handleEmailContent = (event: React.ChangeEvent<any>) => {
    const { name, value } = event.target;
    dispatch(setReduxSignRequest({ [name]: value }));
    if (name === "emailSubject" && isTruthy(value)) {
      dispatch(setErrors({ ...errors, emailSubject: "" }));
    }
  };

  const handleReminderDaysOnChange = (event: React.ChangeEvent<any>) => {
    const { value, name } = event.target;
    if (value >= 0) {
      dispatch(
        setReduxSignRequest({
          reminders: {
            ...signRequest.reminders,
            [name]: value.replace(/[^0-9]/g, ""),
          },
        }),
      );
      value > 0 && dispatch(setErrors({ ...errors, [name]: "" }));
    }
  };

  const handleExpirationDaysOnChange = (event: React.ChangeEvent<any>) => {
    const { value, name } = event.target;
    if (value >= 0) {
      dispatch(setReduxSignRequest({ [name]: value.replace(/[^0-9]/g, "") }));
      value > 0 && dispatch(setErrors({ ...errors, [name]: "" }));
    }
  };

  const handlePermissionChange = (name: string, checked: boolean) => {
    dispatch(
      setReduxSignRequest({
        signRequestConfig: {
          ...signRequest.signRequestConfig,
          [name]: checked,
        },
      }),
    );
  };

  /** "Attach signed copies" only makes sense while the completion mail is on. */
  const isPermissionDisabled = (key: string) => {
    if (key === "allowSignatureDelegation") return !isDelegationAvailable;
    if (signRequest.signRequestConfig == null) return false;
    return (
      key === "attachDocumentToMailOnCompletion" &&
      !signRequest.signRequestConfig.sendNotificationOnCompletion
    );
  };

  const isPermissionOn = (key: string) => {
    const config = signRequest.signRequestConfig as any;
    const value = config ? config[key] : true;
    if (key === "allowSignatureDelegation") {
      return isDelegationAvailable && !!value;
    }
    return !!value;
  };

  const permissionTooltip = (key: string) =>
    key === "allowSignatureDelegation" && !isDelegationAvailable
      ? getFeatureTooltip(delegationState)
      : "";

  const handleAccept = (val: any) => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Sent back in the backend's zone (UTC) — see BACKEND_TIMEZONE.
    const estDateTime = moment.tz(val, userTimeZone).tz(BACKEND_TIMEZONE);
    if (val && isTruthy(estDateTime?.format("MM/DD/YYYY HH:mm:ss"))) {
      props.setOpenDatePicker(false);
      if (props.isScheduleReq) {
        if (isScheduleBeforeAlldelayedSigners(val)) {
          dispatch(setScheduleTime(estDateTime.format("MM/DD/YYYY HH:mm:ss")));
          dispatch(setErrors({ schedule: "" }));
        } else {
          openErrorNotification(
            "Date/time must precede all of the delayed signer's date/time",
          );
        }
      } else {
        delaySigner(estDateTime, val);
        dispatch(setErrors({ delay: "" }));
      }
      props.setIsDelaySigner(false);
      props.setIsScheduleRequest(false);
    }
  };

  const delaySigner = (estDateTime: Moment, val: Moment | null) => {
    const envelopeDate = signRequest.scheduleTime;
    const envelopeDateInLocalTz = convertEstToLocalTimezone(
      signRequest.scheduleTime!,
      "MM/DD/YYYY HH:mm:ss",
    );
    const updateSignerDateTime = () => {
      dispatch(
        updateIndividualSigner({
          signerId: props.signerToDelay.id,
          fieldsToUpdate: {
            delayDays: undefined,
            delayHours: undefined,
            scheduleTime: estDateTime.format("MM/DD/YYYY HH:mm:ss"),
          },
        }),
      );
    };

    if (envelopeDate) {
      if (envelopeDateInLocalTz.isBefore(val)) {
        updateSignerDateTime();
      } else {
        openErrorNotification(
          "Delayed signer date/time must be after scheduled envelope date/time.",
        );
      }
    } else {
      updateSignerDateTime();
    }
  };

  const isScheduleBeforeAlldelayedSigners = (envelopeDate: Moment | null) =>
    signRequest.signers.every((signer: any) => {
      if (signer.scheduleTime) {
        return envelopeDate?.isBefore(
          convertEstToLocalTimezone(signer.scheduleTime, "MM/DD/YYYY HH:mm:ss"),
        );
      }
      return true;
    });

  const openSchedulePicker = () => {
    props.setOpenDatePicker(true);
    props.setIsScheduleRequest(true);
  };

  const chooseSendNow = () => {
    setWantsSchedule(false);
    if (isTruthy(signRequest.scheduleTime)) {
      dispatch(setScheduleTime(undefined));
      dispatch(setErrors({ ...errors, schedule: "" }));
    }
  };

  const chooseScheduleLater = () => {
    if (!isScheduleAvailable) return;
    setWantsSchedule(true);
    if (!isTruthy(signRequest.scheduleTime)) openSchedulePicker();
  };

  /* -------------------------------------------------------------- summaries */

  const subject = signRequest.emailSubject ?? "";
  const message = signRequest.emailMessage ?? "";
  const subjectError = !isTruthy(subject) && isTruthy(errors.emailSubject);
  const needReminders = isReminderAvailable && !!signRequest.needReminders;
  const firstReminder = signRequest.reminders?.daysBeforeFirstReminder ?? "";
  const followUp = signRequest.reminders?.daysBetweenReminders ?? "";
  const expireDays = signRequest.expirationDays ?? "";
  const expireNote = `Expires on ${moment()
    .add(Number(expireDays) || 0, "days")
    .format("MMMM D, YYYY")}`;
  // Inside the embed widget a signer never leaves the partner's app: delegation (a link sent to someone
  // else) and reminder emails do not apply, so those two are not offered there.
  const isEmbed = isEmbedEditorMode();
  const permissions = isEmbed
    ? PERMISSIONS.filter((p) => p.key !== "allowSignatureDelegation")
    : PERMISSIONS;
  const enabledPermissions = permissions.filter((p) =>
    isPermissionOn(p.key),
  ).length;
  const scheduledLabel = isTruthy(signRequest.scheduleTime)
    ? convertEstToLocalTimezone(
        signRequest.scheduleTime!,
        "MM/DD/YYYY HH:mm:ss",
      ).format("MMM DD, YYYY HH:mm")
    : "";

  const SkeletonLoader = () => (
    <Box sx={styles.page}>
      <Skeleton variant="text" width={280} height={38} />
      <Skeleton variant="text" width={430} height={20} sx={{ mb: "26px" }} />
      <Box sx={styles.layout}>
        <Box sx={styles.sections}>
          {[0, 1, 2, 3].map((index) => (
            <Skeleton
              key={index}
              variant="rounded"
              height={index === 0 ? 250 : 68}
              sx={{ borderRadius: "12px" }}
            />
          ))}
        </Box>
        <Skeleton
          variant="rounded"
          height={430}
          sx={{ borderRadius: "14px", width: "100%" }}
        />
      </Box>
    </Box>
  );

  if (loading) return <SkeletonLoader />;

  return (
    <Box sx={styles.page}>
      <Typography component="h1" sx={styles.title}>
        Email configurations
      </Typography>
      <Typography component="p" sx={styles.subtitle}>
        Set delivery, reminders and signer permissions, and preview the email
        your recipients receive.
      </Typography>

      <Box sx={styles.layout}>
        <Box sx={styles.sections}>
          {/* 1 — EMAIL CONTENT */}
          <EmailConfigSection
            icon={MailGlyph}
            title="Email content"
            subtitle="Subject and personal message sent to signers"
            badge={isTruthy(subject) ? undefined : "Subject required"}
            open={open.content}
            onToggle={() => toggleSection("content")}
          >
            <Box sx={styles.field}>
              <Box sx={styles.fieldLabelRow}>
                <Typography component="span" sx={styles.fieldLabel}>
                  Email subject{" "}
                  <Box component="span" sx={styles.fieldRequired}>
                    *
                  </Box>
                </Typography>
                <Typography component="span" sx={styles.fieldCounter}>
                  {subject.length}/{SUBJECT_MAX}
                </Typography>
              </Box>
              <InputBase
                id="email_subject"
                name="emailSubject"
                value={subject}
                onChange={handleEmailContent}
                placeholder="Subject your signers will see"
                inputProps={{
                  maxLength: SUBJECT_MAX,
                  "aria-label": "Email subject",
                  "aria-required": true,
                  "aria-invalid": subjectError,
                }}
                sx={styles.subjectInput(subjectError)}
              />
            </Box>

            <Box sx={styles.field}>
              <Box sx={styles.fieldLabelRow}>
                <Typography component="span" sx={styles.fieldLabel}>
                  Email message
                </Typography>
                <Typography component="span" sx={styles.fieldCounter}>
                  {message.length}/{MESSAGE_MAX}
                </Typography>
              </Box>
              <InputBase
                id="email_message"
                name="emailMessage"
                value={message}
                onChange={handleEmailContent}
                placeholder="Add a personal note to your recipients (optional)"
                multiline
                minRows={4}
                inputProps={{
                  maxLength: MESSAGE_MAX,
                  "aria-label": "Email message",
                }}
                sx={styles.messageInput}
              />
            </Box>
          </EmailConfigSection>

          {/* 2 — REMINDERS & EXPIRATION */}
          <EmailConfigSection
            icon={ClockGlyph}
            title={isEmbed ? "Expiration" : "Reminders & expiration"}
            subtitle={
              isEmbed
                ? "Set a deadline for the request"
                : "Nudge signers automatically and set a deadline"
            }
            badge={
              isEmbed
                ? `Expires in ${expireDays}`
                : needReminders
                  ? `Every ${followUp} day(s) · expires in ${expireDays}`
                  : `No reminders · expires in ${expireDays}`
            }
            open={open.reminders}
            onToggle={() => toggleSection("reminders")}
          >
            {!isEmbed && (
              <>
            <Box sx={styles.settingRow}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="span"
                  sx={{ ...styles.settingLabel(), display: "block" }}
                >
                  Automatic reminders
                </Typography>
                <Typography
                  component="span"
                  sx={{ ...styles.settingDesc, display: "block" }}
                >
                  Email signers who haven't signed yet
                </Typography>
              </Box>
              <Tooltip
                describeChild
                title={getFeatureTooltip(reminderState)}
                disableHoverListener={isReminderAvailable}
                arrow
              >
                <Box component="span" sx={{ display: "flex", gap: "8px" }}>
                  {!isReminderAvailable && (
                    <LockOutlinedIcon
                      sx={{ fontSize: lock.size, color: lock.color }}
                    />
                  )}
                  <EmailConfigSwitch
                    id="needReminders"
                    label="Automatic reminders"
                    checked={needReminders}
                    disabled={!isReminderAvailable}
                    onChange={(checked) =>
                      dispatch(setReduxSignRequest({ needReminders: checked }))
                    }
                  />
                </Box>
              </Tooltip>
            </Box>

            {needReminders && (
              <Box sx={styles.nestedPanel}>
                <Box sx={styles.nestedRow}>
                  <Typography component="span" sx={styles.nestedLabel()}>
                    First reminder after
                  </Typography>
                  <InputBase
                    id="first_reminder"
                    name="daysBeforeFirstReminder"
                    value={firstReminder}
                    onChange={handleReminderDaysOnChange}
                    inputProps={{
                      maxLength: 3,
                      inputMode: "numeric",
                      "aria-label": "First reminder after (days)",
                      "aria-invalid": isTruthy(errors.daysBeforeFirstReminder),
                    }}
                    sx={styles.numberInput(
                      false,
                      isTruthy(errors.daysBeforeFirstReminder),
                    )}
                  />
                  <Typography component="span" sx={styles.unit}>
                    day(s)
                  </Typography>
                </Box>
                <Box sx={styles.nestedRow}>
                  <Typography component="span" sx={styles.nestedLabel()}>
                    Follow-up reminders every
                  </Typography>
                  <InputBase
                    id="reminder_frequency"
                    name="daysBetweenReminders"
                    value={followUp}
                    onChange={handleReminderDaysOnChange}
                    inputProps={{
                      maxLength: 3,
                      inputMode: "numeric",
                      "aria-label": "Follow-up reminders every (days)",
                      "aria-invalid": isTruthy(errors.daysBetweenReminders),
                    }}
                    sx={styles.numberInput(
                      false,
                      isTruthy(errors.daysBetweenReminders),
                    )}
                  />
                  <Typography component="span" sx={styles.unit}>
                    day(s)
                  </Typography>
                </Box>
              </Box>
            )}

            <Box sx={styles.divider} />
              </>
            )}

            <Tooltip
              describeChild
              title={getFeatureTooltip(autoExpirationState)}
              disableHoverListener={isAutoExpirationAvailable}
              arrow
            >
              <Box sx={styles.settingRow}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    component="span"
                    sx={{
                      ...styles.settingLabel(!isAutoExpirationAvailable),
                      display: "block",
                    }}
                  >
                    Request expires after
                  </Typography>
                  <Typography
                    component="span"
                    sx={{ ...styles.settingDesc, display: "block" }}
                  >
                    {expireNote}
                  </Typography>
                </Box>
                {!isAutoExpirationAvailable && (
                  <LockOutlinedIcon
                    sx={{ fontSize: lock.size, color: lock.color }}
                  />
                )}
                <InputBase
                  id="expiry_days"
                  name="expirationDays"
                  value={expireDays}
                  onChange={handleExpirationDaysOnChange}
                  disabled={!isAutoExpirationAvailable}
                  inputProps={{
                    maxLength: 3,
                    inputMode: "numeric",
                    "aria-label": "Request expires after (days)",
                    "aria-invalid": isTruthy(errors.expirationDays),
                  }}
                  sx={styles.numberInput(true, isTruthy(errors.expirationDays))}
                />
                <Typography component="span" sx={styles.unit}>
                  day(s)
                </Typography>
              </Box>
            </Tooltip>
          </EmailConfigSection>

          {/* 3 — SIGNER PERMISSIONS */}
          <EmailConfigSection
            icon={ShieldCheckGlyph}
            title="Signer permissions"
            subtitle="What signers can do with this request"
            badge={`${enabledPermissions} of ${permissions.length} enabled`}
            open={open.options}
            onToggle={() => toggleSection("options")}
            tight
          >
            {permissions.map((permission, index) => {
              const disabled = isPermissionDisabled(permission.key);
              const tooltip = permissionTooltip(permission.key);
              return (
                <Box
                  key={permission.key}
                  sx={styles.optionRow(index === permissions.length - 1)}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      component="span"
                      sx={{
                        ...styles.settingLabel(disabled),
                        display: "block",
                      }}
                    >
                      {permission.label}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{ ...styles.settingDesc, display: "block" }}
                    >
                      {permission.desc}
                    </Typography>
                  </Box>
                  <Tooltip
                    describeChild
                    title={tooltip}
                    disableHoverListener={!tooltip}
                    arrow
                  >
                    <Box component="span" sx={{ display: "flex", gap: "8px" }}>
                      {!!tooltip && (
                        <LockOutlinedIcon
                          sx={{ fontSize: lock.size, color: lock.color }}
                        />
                      )}
                      <EmailConfigSwitch
                        id={permission.key}
                        label={permission.label}
                        checked={isPermissionOn(permission.key)}
                        disabled={disabled}
                        onChange={(checked) =>
                          handlePermissionChange(permission.key, checked)
                        }
                      />
                    </Box>
                  </Tooltip>
                </Box>
              );
            })}
          </EmailConfigSection>

          {/* 4 — SCHEDULE */}
          <EmailConfigSection
            icon={CalendarGlyph}
            title="Schedule sign request"
            subtitle="Send now or pick a later date"
            badge={
              wantsSchedule
                ? isTruthy(signRequest.scheduleTime)
                  ? "Scheduled"
                  : "Pick a date"
                : "Sends immediately"
            }
            open={open.schedule}
            onToggle={() => toggleSection("schedule")}
          >
            <Typography component="div" sx={styles.scheduleIntro}>
              Send now, or schedule this request to go out later. Reminders and
              expiration count from the send time.
            </Typography>

            <Box
              sx={styles.scheduleCards}
              role="group"
              aria-label="Choose when to send"
            >
              <ButtonBase
                disableRipple
                onClick={chooseSendNow}
                aria-pressed={!wantsSchedule}
                sx={styles.scheduleCard(!wantsSchedule)}
              >
                <Box sx={styles.scheduleRadio(!wantsSchedule)}>
                  <Box sx={styles.scheduleRadioDot(!wantsSchedule)} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    component="span"
                    sx={{ ...styles.scheduleCardLabel, display: "block" }}
                  >
                    Send immediately
                  </Typography>
                  <Typography
                    component="span"
                    sx={{ ...styles.scheduleCardSub, display: "block" }}
                  >
                    As soon as you hit Send
                  </Typography>
                </Box>
              </ButtonBase>

              <Tooltip
                describeChild
                title={getFeatureTooltip(scheduleState)}
                disableHoverListener={isScheduleAvailable}
                arrow
              >
                <Box component="span" sx={{ flex: 1, display: "flex" }}>
                  <ButtonBase
                    disableRipple
                    onClick={chooseScheduleLater}
                    aria-pressed={wantsSchedule}
                    disabled={!isScheduleAvailable}
                    sx={styles.scheduleCard(wantsSchedule)}
                  >
                    <Box sx={styles.scheduleRadio(wantsSchedule)}>
                      <Box sx={styles.scheduleRadioDot(wantsSchedule)} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        component="span"
                        sx={{ ...styles.scheduleCardLabel, display: "block" }}
                      >
                        Schedule for later
                      </Typography>
                      <Typography
                        component="span"
                        sx={{ ...styles.scheduleCardSub, display: "block" }}
                      >
                        Pick a date and time
                      </Typography>
                    </Box>
                    {!isScheduleAvailable && (
                      <LockOutlinedIcon
                        sx={{
                          fontSize: lock.size,
                          color: lock.color,
                          ml: "auto",
                        }}
                      />
                    )}
                  </ButtonBase>
                </Box>
              </Tooltip>
            </Box>

            {wantsSchedule && (
              <Box sx={styles.schedulePicked}>
                <CalendarSmallGlyph
                  sx={{
                    width: schedule.pickedIconSize,
                    height: schedule.pickedIconSize,
                    color: schedule.pickedIconColor,
                    flexShrink: 0,
                  }}
                />
                <ButtonBase
                  disableRipple
                  id="schedule-envelope-btn"
                  onClick={openSchedulePicker}
                  aria-label={
                    scheduledLabel
                      ? `Scheduled send date and time: ${scheduledLabel}. Activate to change.`
                      : "Pick a date and time"
                  }
                  sx={styles.schedulePickedField}
                >
                  {scheduledLabel || "Pick a date and time"}
                </ButtonBase>
                <Typography component="span" sx={styles.schedulePickedNote}>
                  {schedule.pickedNote}
                </Typography>
              </Box>
            )}
          </EmailConfigSection>

          {/* eSign mode — not in the design, kept so the requester can still
              choose (and see the remaining quota for) each signing mode. */}
          {/* <EmailConfigSection
            icon={PeopleGlyph}
            title={urls.PROD ? "eSign mode" : "Select the eSign mode"}
            subtitle="How this request is signed and certified"
            badge={
              selectedValue === strings.selfSigned
                ? "Basic eSign"
                : "Certified eSign"
            }
            open={open.esign}
            onToggle={() => toggleSection("esign")}
          >
            <RadioGroup
              aria-label="eSign mode"
              value={selectedValue}
              onChange={(event) => setSignType(event.target.value)}
            >
              <Stack direction="column" spacing={2}>
                <Stack direction="column" spacing={0.5}>
                  <Box sx={styles.settingRow}>
                    {urls.PROD ? (
                      <Typography
                        component="span"
                        sx={styles.settingLabel()}
                      >
                        Certified eSign
                      </Typography>
                    ) : (
                      <FormControlLabel
                        value={strings.certifiedSign}
                        disabled={isDisable}
                        control={<Radio color="primary" disableRipple />}
                        label={
                          <Typography
                            component="span"
                            sx={styles.settingLabel()}
                          >
                            Certified eSign
                          </Typography>
                        }
                        sx={styles.esignRadio}
                      />
                    )}
                    <Typography component="span" sx={styles.esignRemaining}>
                      {isUnlimitedLimit(CertifiedSignedStats?.limit) ? (
                        <UnlimitedLabel />
                      ) : (
                        `${formatCount(certifiedRemaining)} left`
                      )}
                    </Typography>
                  </Box>
                  <Typography component="div" sx={styles.settingDesc}>
                    A secure electronic signature with audit trail plus a digital
                    certificate that verifies the signer's identity. Ideal for
                    legal contracts, financial agreements, real estate deals, and
                    any important document needing authenticity and compliance.
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography component="span" sx={styles.settingDesc}>
                      Security Level:
                    </Typography>
                    <Chip label="High" size="small" sx={styles.esignChip(true)} />
                  </Stack>
                </Stack>

                {!urls.PROD && (
                  <Stack direction="column" spacing={0.5}>
                    <Box sx={styles.settingRow}>
                      <FormControlLabel
                        value={strings.selfSigned}
                        disabled={isDisable}
                        control={<Radio color="primary" disableRipple />}
                        label={
                          <Typography
                            component="span"
                            sx={styles.settingLabel()}
                          >
                            Basic eSign
                          </Typography>
                        }
                        sx={styles.esignRadio}
                      />
                      <Typography component="span" sx={styles.esignRemaining}>
                        {isUnlimitedLimit(selfSignedStats?.limit) ? (
                          <UnlimitedLabel />
                        ) : (
                          `${formatCount(selfSignRemaining)} left`
                        )}
                      </Typography>
                    </Box>
                    <Typography component="div" sx={styles.settingDesc}>
                      A simple electronic signature with audit trail (like
                      timestamps and signer details), no digital certificate.
                      Best for internal approvals, HR forms, NDAs, and any
                      low-risk agreements where speed is important.
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography component="span" sx={styles.settingDesc}>
                        Security Level:
                      </Typography>
                      <Chip
                        label="Basic"
                        size="small"
                        sx={styles.esignChip(false)}
                      />
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </RadioGroup>
          </EmailConfigSection> */}

          {/* Web forms — not in the design; only offered for template-backed
              requests that have active forms to assign. */}
          {isFromTemplate && (
            <EmailConfigSection
              icon={DocumentGlyph}
              title="Web forms"
              subtitle="Collect answers before a signer signs"
              badge={
                isWebFormsAvailable && useWebForms ? "In use" : "Not in use"
              }
              open={open.webforms}
              onToggle={() => toggleSection("webforms")}
            >
              <Box sx={styles.settingRow}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    component="span"
                    sx={{
                      ...styles.settingLabel(!isWebFormsAvailable),
                      display: "block",
                    }}
                  >
                    Use Web Form for signers
                  </Typography>
                  <Typography
                    component="span"
                    sx={{ ...styles.settingDesc, display: "block" }}
                  >
                    Optionally assign a web form to each signer. Signers will
                    fill out the form before signing.
                  </Typography>
                </Box>
                <Tooltip
                  describeChild
                  title={getFeatureTooltip(webFormsState)}
                  disableHoverListener={isWebFormsAvailable}
                  arrow
                >
                  <Box component="span" sx={{ display: "flex", gap: "8px" }}>
                    {!isWebFormsAvailable && (
                      <LockOutlinedIcon
                        sx={{ fontSize: lock.size, color: lock.color }}
                      />
                    )}
                    <EmailConfigSwitch
                      id="useWebFormsSwitch"
                      label="Use Web Form for signers"
                      checked={isWebFormsAvailable && useWebForms}
                      disabled={!isWebFormsAvailable}
                      onChange={handleToggleWebForms}
                    />
                  </Box>
                </Tooltip>
              </Box>

              {useWebForms &&
                (webFormsLoading ? (
                  <Skeleton variant="rounded" height={40} />
                ) : availableWebForms.length === 0 ? (
                  <Typography component="div" sx={styles.settingDesc}>
                    No web forms are available for this template.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {(signRequest.signers || []).map(
                      (signer: RequestParticipant) => {
                        const optionsForSigner = getWebFormsForSigner(signer);
                        const signerLabel =
                          signer.name ||
                          signer.requestRole ||
                          `Signer ${signer.order}`;
                        return (
                          <Stack
                            key={signer.id}
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.5}
                            alignItems={{ xs: "stretch", sm: "center" }}
                          >
                            <Box
                              sx={{
                                minWidth: 170,
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: "50%",
                                  backgroundColor: signer.color || "#999",
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                component="span"
                                noWrap
                                title={signerLabel}
                                sx={styles.settingLabel()}
                              >
                                {signerLabel}
                              </Typography>
                            </Box>
                            <Box sx={{ flex: 1, width: "100%" }}>
                              <CustomAutoComplete
                                id={`webform-signer-${signer.id}`}
                                ariaLabel={`Web form for ${signerLabel}`}
                                placeHolder={
                                  optionsForSigner.length === 0
                                    ? "No web form available for this signer"
                                    : "Select web form (optional)"
                                }
                                options={optionsForSigner}
                                value={
                                  optionsForSigner.find(
                                    (f) => f.id === signer.webFormId,
                                  ) ?? null
                                }
                                onChange={(_e: any, newValue: any) =>
                                  handleWebFormChange(
                                    signer.id,
                                    newValue?.id ?? "",
                                  )
                                }
                                getOptionLabel={(option: any) =>
                                  option?.name ?? ""
                                }
                                freeSolo={false}
                                disabled={optionsForSigner.length === 0}
                                showDropdownIconWhenBlurred
                              />
                            </Box>
                          </Stack>
                        );
                      },
                    )}
                  </Stack>
                ))}
            </EmailConfigSection>
          )}
        </Box>

        <Box sx={{ minWidth: 0, width: "100%", maxWidth: layout.previewWidth }}>
          <EmailPreviewComponent signRequest={signRequest} />
        </Box>
      </Box>

      {/* No visible heading in the design — the shell takes an accessible name
          instead and its header collapses to the close affordance. */}
      <AppModal
        open={props.openDatePicker}
        onClose={() => {
          props.setOpenDatePicker(false);
          props.setIsDelaySigner(false);
          props.setIsScheduleRequest(false);
        }}
        ariaLabel="Select a date and time"
        size="md"
      >
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <MyDateTimePicker handleAccept={handleAccept} />
        </LocalizationProvider>
      </AppModal>
      <UpgradeModal
        open={upgradeModal.open}
        featureKey={upgradeModal.featureKey}
        onClose={() => setUpgradeModal({ open: false })}
      />
    </Box>
  );
};

export default RequestConfiguration;
